<script setup>
// Imports
import OmniDialogHeader from 'components/UI/Dialogs/OmniDialogHeader.vue'
// Props
defineProps({ dialogIcon: { type: String, default: 'palette' } })

// Models
const model = defineModel({ type: String })

// Constants
const $q = useQuasar()
const attrs = useAttrs()
const emits = defineEmits(['update:value'])

// Refs
const formElement = ref(null)
const showColorDialog = ref(false)
const selectedColor = ref('#000000')
const storedRange = ref(null)
const isShiftPressed = ref(false)
const editorFocused = ref(false)

// Computed
const editorAttrs = computed(() => {
  if (attrs.toolbar !== undefined) {
    return attrs
  }

  const { toolbar, ...rest } = attrs
  return rest
})

// Methods
const insertImageAtCursor = (imageUrl) => {
  const editor = formElement.value
  if (!editor) return

  const editorEl = editor.$el.querySelector('.q-editor__content')
  if (!editorEl) return

  const img = document.createElement('img')
  img.src = imageUrl
  img.style.maxWidth = '100%' // Ensure the image fits within the editor

  const selection = window.getSelection()
  if (selection.rangeCount > 0) {
    const range = selection.getRangeAt(0)
    range.deleteContents() // Remove any selected text
    range.insertNode(img)

    range.setStartAfter(img)
    range.setEndAfter(img)
    selection.removeAllRanges()
    selection.addRange(range)
  }
}

const processImage = (file) => {
  const reader = new FileReader()
  reader.onload = (e) => {
    const base64Image = e.target.result
    insertImageAtCursor(base64Image)
  }
  reader.readAsDataURL(file)
}

const handleQEditorPaste = (event) => {
  const items = event.clipboardData.items
  for (let item of items) {
    if (item.type.indexOf('image') !== -1) {
      const file = item.getAsFile()
      processImage(file)
      event.preventDefault()
      break
    }
  }
}

const handleQEditorDrop = (event) => {
  const files = event.dataTransfer.files
  for (let file of files) {
    if (file.type.indexOf('image') !== -1) {
      processImage(file)
      event.preventDefault()
      break
    }
  }
}

const placeCursorAfterImage = (img) => {
  if (!img) return
  const range = document.createRange()
  range.setStartAfter(img)
  range.collapse(true)
  const selection = window.getSelection()
  selection.removeAllRanges()
  selection.addRange(range)
}

const enableImageResizeInDiv = (editor) => {
  if (!(/chrome/i.test(navigator.userAgent) && /google/i.test(window.navigator.vendor))) {
    return
  }

  let resizing = false
  let currentImage
  let createDOM = function (elementType, className, styles) {
    let ele = document.createElement(elementType)
    ele.className = className
    setStyle(ele, styles)
    return ele
  }

  let setStyle = function (ele, styles) {
    for (const key in styles) {
      ele.style[key] = styles[key]
    }
    return ele
  }

  let removeResizeFrame = function () {
    document.querySelectorAll('.resize-frame,.resizer').forEach((item) => item.parentNode.removeChild(item))
  }

  const removeUndoButton = () => {
    const undoButton = document.querySelector('.undo-button')
    if (undoButton) {
      undoButton.remove()
    }
  }

  const resetImageSize = (img) => {
    img.style.width = ''
    img.style.height = ''

    removeUndoButton()
    removeResizeFrame()
  }

  const handleOutsideImgClick = (event) => {
    if (resizing) {
      return
    }

    if (currentImage && (event.target === currentImage || event.target.closest('.undo-button'))) {
      return
    }

    removeUndoButton()
    removeResizeFrame()

    document.removeEventListener('click', handleOutsideImgClick)
  }

  let offset = function offset(el) {
    const rect = el.getBoundingClientRect(),
      scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
      scrollTop = window.pageYOffset || document.documentElement.scrollTop
    return { top: rect.top + scrollTop, left: rect.left + scrollLeft }
  }

  let clickImage = function (img) {
    removeResizeFrame()
    removeUndoButton()

    currentImage = img
    const imgHeight = img.offsetHeight
    const imgWidth = img.offsetWidth
    const imgPosition = { top: img.offsetTop, left: img.offsetLeft }
    const editorScrollTop = editor.scrollTop
    const editorScrollLeft = editor.scrollLeft
    const top = imgPosition.top - editorScrollTop - 1
    const left = imgPosition.left - editorScrollLeft - 1

    if (img.style.width || img.style.height) {
      let undoButton = document.createElement('div')
      undoButton.className = 'undo-button text-black'
      undoButton.style.position = 'absolute'
      undoButton.style.top = top + 8 + 'px'
      undoButton.style.left = left + imgWidth - 32 + 'px'
      undoButton.style.padding = '4px'
      undoButton.style.borderRadius = '50%'
      undoButton.style.cursor = 'pointer'
      undoButton.style.backgroundColor = 'white'
      undoButton.style.fontSize = '22px'
      undoButton.style.boxShadow = '0px 0px 4px rgba(0, 0, 0, 0.2)'
      undoButton.style.zIndex = '10'
      undoButton.style.display = 'flex'
      undoButton.style.alignItems = 'center'
      undoButton.style.justifyContent = 'center'
      undoButton.style.width = '26px'
      undoButton.style.height = '26px'

      const qIcon = document.createElement('i')
      qIcon.className = 'q-icon material-symbols-outlined'
      qIcon.innerText = 'undo'

      undoButton.appendChild(qIcon)

      undoButton.onclick = () => resetImageSize(img)

      editor.append(undoButton)

      document.removeEventListener('click', handleOutsideImgClick)
      document.addEventListener('click', handleOutsideImgClick)
    }

    editor.append(
      createDOM('span', 'resize-frame', {
        margin: '10px',
        position: 'absolute',
        top: top + imgHeight - 10 + 'px',
        left: left + imgWidth - 10 + 'px',
        border: 'solid 3px blue',
        width: '6px',
        height: '6px',
        cursor: 'se-resize',
        zIndex: 1,
      }),
    )

    editor.append(
      createDOM('span', 'resizer top-border', {
        position: 'absolute',
        top: top + 'px',
        left: left + 'px',
        border: 'dashed 1px grey',
        width: imgWidth + 'px',
        height: '0px',
      }),
    )

    editor.append(
      createDOM('span', 'resizer left-border', {
        position: 'absolute',
        top: top + 'px',
        left: left + 'px',
        border: 'dashed 1px grey',
        width: '0px',
        height: imgHeight + 'px',
      }),
    )

    editor.append(
      createDOM('span', 'resizer right-border', {
        position: 'absolute',
        top: top + 'px',
        left: left + imgWidth + 'px',
        border: 'dashed 1px grey',
        width: '0px',
        height: imgHeight + 'px',
      }),
    )

    editor.append(
      createDOM('span', 'resizer bottom-border', {
        position: 'absolute',
        top: top + imgHeight + 'px',
        left: left + 'px',
        border: 'dashed 1px grey',
        width: imgWidth + 'px',
        height: '0px',
      }),
    )

    document.querySelector('.resize-frame').onmousedown = () => {
      resizing = true
      return false
    }

    editor.onmouseup = () => {
      if (resizing) {
        currentImage.style.width = document.querySelector('.top-border').offsetWidth + 'px'

        if (!isShiftPressed.value) {
          currentImage.style.height = document.querySelector('.left-border').offsetHeight + 'px'
        } else {
          let aspectRatio = currentImage.naturalHeight / currentImage.naturalWidth
          currentImage.style.height = parseFloat(currentImage.style.width) * aspectRatio + 'px'
        }

        refresh()
        currentImage.click()
        resizing = false
      }
    }

    editor.onmousemove = (e) => {
      if (currentImage && resizing) {
        let width = e.pageX - offset(currentImage).left
        width = width < 1 ? 1 : width

        const top = imgPosition.top - editorScrollTop - 1
        const left = imgPosition.left - editorScrollLeft - 1

        setStyle(document.querySelector('.resize-frame'), {
          left: left + width - 10 + 'px',
        })

        setStyle(document.querySelector('.top-border'), { width: width + 'px' })

        if (!isShiftPressed.value) {
          let height = e.pageY - offset(currentImage).top
          height = height < 1 ? 1 : height

          setStyle(document.querySelector('.resize-frame'), { top: top + height - 10 + 'px' })
          setStyle(document.querySelector('.left-border'), { height: height + 'px' })
          setStyle(document.querySelector('.right-border'), {
            left: left + width + 'px',
            height: height + 'px',
          })
          setStyle(document.querySelector('.bottom-border'), {
            top: top + height + 'px',
            width: width + 'px',
          })
        } else {
          let aspectRatio = currentImage.naturalHeight / currentImage.naturalWidth
          let height = width * aspectRatio

          setStyle(document.querySelector('.resize-frame'), { top: top + height - 10 + 'px' })
          setStyle(document.querySelector('.left-border'), { height: height + 'px' })
          setStyle(document.querySelector('.right-border'), {
            left: left + width + 'px',
            height: height + 'px',
          })
          setStyle(document.querySelector('.bottom-border'), {
            top: top + height + 'px',
            width: width + 'px',
          })
        }
      }
      return false
    }

    placeCursorAfterImage(img)
  }

  let bindClickListener = function () {
    editor.querySelectorAll('img').forEach((img, i) => {
      img.onclick = (e) => {
        if (e.target === img) {
          clickImage(img)
        }
      }
    })
  }

  let refresh = function () {
    bindClickListener()
    removeResizeFrame()
    if (!currentImage) {
      return
    }
    let img = currentImage
    let imgHeight = img.offsetHeight
    let imgWidth = img.offsetWidth
    let imgPosition = { top: img.offsetTop, left: img.offsetLeft }
    let editorScrollTop = editor.scrollTop
    let editorScrollLeft = editor.scrollLeft
    const top = imgPosition.top - editorScrollTop - 1
    const left = imgPosition.left - editorScrollLeft - 1

    editor.append(
      createDOM('span', 'resize-frame', {
        position: 'absolute',
        top: top + imgHeight + 'px',
        left: left + imgWidth + 'px',
        border: 'solid 2px red',
        width: '6px',
        height: '6px',
        cursor: 'se-resize',
        zIndex: 1,
      }),
    )

    editor.append(
      createDOM('span', 'resizer', {
        position: 'absolute',
        top: top + 'px',
        left: left + 'px',
        border: 'dashed 1px grey',
        width: imgWidth + 'px',
        height: '0px',
      }),
    )

    editor.append(
      createDOM('span', 'resizer', {
        position: 'absolute',
        top: top + 'px',
        left: left + imgWidth + 'px',
        border: 'dashed 1px grey',
        width: '0px',
        height: imgHeight + 'px',
      }),
    )

    editor.append(
      createDOM('span', 'resizer', {
        position: 'absolute',
        top: top + imgHeight + 'px',
        left: left + 'px',
        border: 'dashed 1px grey',
        width: imgWidth + 'px',
        height: '0px',
      }),
    )
  }

  let reset = function () {
    if (currentImage != null) {
      currentImage = null
      resizing = false
      removeResizeFrame()
    }
    bindClickListener()
  }

  editor.addEventListener(
    'scroll',
    function () {
      reset()
    },
    false,
  )

  editor.addEventListener('mouseup', function (e) {
    if (!resizing) {
      const x = e.x ? e.x : e.clientX
      const y = e.y ? e.y : e.clientY
      let mouseUpElement = document.elementFromPoint(x, y)
      if (mouseUpElement) {
        let matchingElement = null
        if (mouseUpElement.tagName === 'IMG') {
          matchingElement = mouseUpElement
        }
        if (!matchingElement) {
          reset()
        } else {
          clickImage(matchingElement)
        }
      }
    }
  })

  editor.addEventListener('keyup', (e) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      // Wacht even zodat de standaardverwijdering van de q-editor zijn werk kan doen
      setTimeout(() => {
        if (currentImage && !editor.contains(currentImage)) {
          removeUndoButton()
          reset()
        }
      }, 10)
    }
  })
}

const applyColor = () => {
  const editor = formElement.value
  if (!editor) return

  const editorEl = editor.$el.querySelector('.q-editor__content')
  if (!editorEl) return

  editorEl.focus()
  const selection = document.getSelection()
  selection.removeAllRanges()
  selection.addRange(storedRange.value)

  const color = selectedColor.value

  try {
    const span = document.createElement('span')
    span.style.color = color
    storedRange.value.surroundContents(span)
  } catch (e) {
    applyColorToRange(storedRange.value, color)
  }
  showColorDialog.value = false
}

function applyColorToRange(range, color) {
  const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      return range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
    },
  })

  const textNodes = []
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode)
  }

  textNodes.forEach((node) => {
    const nodeRange = document.createRange()
    nodeRange.selectNodeContents(node)

    if (node === range.startContainer) {
      nodeRange.setStart(node, range.startOffset)
    }
    if (node === range.endContainer) {
      nodeRange.setEnd(node, range.endOffset)
    }

    if (nodeRange.toString() === node.textContent) {
      const span = document.createElement('span')
      span.style.color = color
      try {
        nodeRange.surroundContents(span)
      } catch (err) {
        wrapPartialTextNode(node, range, color)
      }
    } else {
      wrapPartialTextNode(node, range, color)
    }
  })
}

function wrapPartialTextNode(textNode, range, color) {
  const text = textNode.textContent
  let start = 0,
    end = text.length

  if (textNode === range.startContainer) {
    start = range.startOffset
  }
  if (textNode === range.endContainer) {
    end = range.endOffset
  }

  const beforeText = text.slice(0, start)
  const selectedText = text.slice(start, end)
  const afterText = text.slice(end)

  const parent = textNode.parentNode
  if (!parent) return

  if (beforeText) {
    parent.insertBefore(document.createTextNode(beforeText), textNode)
  }

  const span = document.createElement('span')
  span.style.color = color
  span.textContent = selectedText
  parent.insertBefore(span, textNode)

  if (afterText) {
    parent.insertBefore(document.createTextNode(afterText), textNode)
  }

  parent.removeChild(textNode)
}

const handleKeyUp = (e) => {
  if (e.key === 'Shift') {
    isShiftPressed.value = false
  }
}

// Watchers
watch(
  () => formElement.value,
  (newValue, oldValue) => {
    if (newValue) {
      const editorEl = newValue.$el.querySelector('.q-editor__content')
      if (editorEl) {
        enableImageResizeInDiv(editorEl)
      }
    }
  },
)

// Lifecycle hooks
onMounted(() => {
  attrs.definitions['colorPicker'] = {
    tip: 'Set Font Color',
    icon: 'format_color_text',
    handler: () => {
      const editor = formElement.value
      const editorEl = editor.$el.querySelector('.q-editor__content')
      editorEl.focus()
      const selection = document.getSelection()
      if (selection.rangeCount > 0) {
        storedRange.value = selection.getRangeAt(0)
      } else {
        storedRange.value = selection.createRange()
      }
      showColorDialog.value = true
    },
  }
})
</script>

<template>
  <q-editor
    @paste.stop="handleQEditorPaste"
    @drop.stop="handleQEditorDrop"
    @keydown.shift="isShiftPressed = true"
    @keyup="handleKeyUp"
    ref="formElement"
    min-height="5rem"
    outlined
    :readonly="$attrs.readonly"
    v-model="model"
    :toolbar="[
      [
        {
          label: $q.lang.editor.formatting,
          icon: $q.iconSet.editor.formatting,
          list: 'no-icons',
          options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
        },
        'bold',
        'italic',
      ],
      ['link'],
      ['undo', 'redo'],
      ['ordered', 'unordered'],
      ['indent'],
      ['fullscreen'],
    ]"
    :class="{ 'q-editor--focused': editorFocused, 'q-editor--readonly': $attrs.readonly }"
    v-bind="editorAttrs"
  />

  <!-- Color Picker -->
  <q-dialog v-model="showColorDialog" persistent>
    <q-card>
      <omni-dialog-header :icon="dialogIcon" :title="$t('Shared.Globals.Color')" :subtitle="$t('Shared.DialogSubtitles.actions.color')">
        <template #actions><q-btn v-close-popup flat round dense icon="close" :aria-label="$t('Shared.Globals.Close')" /></template>
      </omni-dialog-header>
      <q-card-section>
        <q-color v-model="selectedColor" format-model="hex" :default-value="selectedColor" />
      </q-card-section>
      <q-card-actions align="right">
        <q-btn color="negative" :label="$t('Shared.Globals.Cancel')" v-close-popup />
        <q-btn color="primary" :label="$t('Shared.Globals.Apply')" @click="applyColor" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
